/**
 * The send flow of BA-85 (design "Flow"): Send now (or Send again / Try again) only opens
 * the confirm; the confirm makes the one POST. A status answer gets its own message; no
 * answer (network error or 15 s) re-reads status/sentSchedule for 5 s and counts the send
 * as landed only if a node of this schedule with a new identity shows up (M3, no clocks).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CurrentAction } from '../types/currentAction';
import { SentSchedule, isSentScheduleActive } from '../types/sentSchedule';
import { SentScheduleApiError, sentScheduleService } from '../services/sentScheduleService';
import { identityOf, sendLanded, storeReplacedAction } from '../utils/sentSchedule';
import { SendScheduleConfirmDialog } from './SendScheduleConfirmDialog';

/** After no answer, how long the listener is watched for the new node */
export const SEND_REREAD_MS = 5000;
const REREAD_POLL_MS = 200;

export interface SendTarget {
  scheduleId: string;
  name: string;
}

/** An i18n key under sentSchedule.*, shown with the schedule's name */
export type SendErrorKey = 'sendFailed' | 'sendRefused' | 'sendDeleted' | 'sendForbidden' | 'sendServer';

export function sendErrorKey(status: number): SendErrorKey {
  if (status === 400) return 'sendRefused';
  if (status === 403) return 'sendForbidden';
  if (status === 404) return 'sendDeleted';
  if (status >= 500) return 'sendServer';
  return 'sendFailed';
}

interface FlowOptions {
  deviceId: string;
  deviceName: string;
  sentSchedule: SentSchedule | null;
  currentAction: CurrentAction | null;
  actionName: string;
  tabletOffline: boolean;
  onSent?: (target: SendTarget) => void;
}

export interface SendFlow {
  /** Opens the confirm; never writes */
  request: (target: SendTarget) => void;
  /** The schedule whose POST is in flight */
  sendingId: string | null;
  errors: Record<string, SendErrorKey>;
  dialog: React.ReactNode;
}

export function useSendScheduleFlow({
  deviceId,
  deviceName,
  sentSchedule,
  currentAction,
  actionName,
  tabletOffline,
  onSent,
}: FlowOptions): SendFlow {
  const [pending, setPending] = useState<SendTarget | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, SendErrorKey>>({});
  // What the closing dialog keeps showing during its fade-out
  const lastPending = useRef<SendTarget | null>(null);
  if (pending) lastPending.current = pending;
  const sentRef = useRef(sentSchedule);
  sentRef.current = sentSchedule;
  // A send whose id is no longer current (device switch, unmount) reports nothing
  const requestId = useRef(0);
  const onSentRef = useRef(onSent);
  onSentRef.current = onSent;
  // One POST per confirm, even before the disabled button has rendered
  const inFlight = useRef(false);

  // A device switch closes the dialog and clears everything, so nothing goes to another tablet
  useEffect(() => {
    requestId.current += 1;
    inFlight.current = false;
    setPending(null);
    setSendingId(null);
    setErrors({});
  }, [deviceId]);

  useEffect(() => () => { requestId.current += 1; }, []);

  const request = useCallback((target: SendTarget) => {
    if (sendingId) return;
    setErrors((e) => {
      const { [target.scheduleId]: _, ...rest } = e;
      return rest;
    });
    setPending(target);
  }, [sendingId]);

  const cancel = useCallback(() => {
    if (!sendingId) setPending(null);
  }, [sendingId]);

  const confirm = useCallback(async () => {
    const target = pending;
    if (!target || inFlight.current) return;
    inFlight.current = true;
    const id = ++requestId.current;
    const before = identityOf(sentRef.current);
    // Only this browser can later say which action a failed send replaced (C11)
    const replacedAction = currentAction && !isSentScheduleActive(sentRef.current) ? actionName : '';
    setSendingId(target.scheduleId);

    let landed: SentSchedule | null = null;
    let error: SendErrorKey | null = null;
    try {
      landed = await sentScheduleService.send(deviceId, target.scheduleId);
    } catch (err) {
      const status = err instanceof SentScheduleApiError ? err.status : 0;
      if (status > 0) {
        error = sendErrorKey(status);
      } else {
        const deadline = Date.now() + SEND_REREAD_MS;
        while (!sendLanded(before, target.scheduleId, sentRef.current) && Date.now() < deadline) {
          await new Promise((resolve) => setTimeout(resolve, REREAD_POLL_MS));
          if (id !== requestId.current) return;
        }
        if (sendLanded(before, target.scheduleId, sentRef.current)) {
          landed = sentRef.current;
        } else {
          error = 'sendFailed';
        }
      }
    }
    if (id !== requestId.current) return;

    inFlight.current = false;
    setSendingId(null);
    setPending(null);
    if (landed) {
      if (replacedAction) storeReplacedAction(deviceId, identityOf(landed), replacedAction);
      onSentRef.current?.(target);
    } else if (error) {
      setErrors((e) => ({ ...e, [target.scheduleId]: error as SendErrorKey }));
    }
  }, [pending, currentAction, actionName, deviceId]);

  const shown = pending ?? lastPending.current;
  const dialog = (
    <SendScheduleConfirmDialog
      open={!!pending}
      scheduleId={shown?.scheduleId ?? ''}
      name={shown?.name ?? ''}
      deviceName={deviceName}
      sentSchedule={sentSchedule}
      currentAction={currentAction}
      actionName={actionName}
      tabletOffline={tabletOffline}
      sending={!!sendingId}
      onConfirm={confirm}
      onCancel={cancel}
    />
  );

  return { request, sendingId, errors, dialog };
}
